<script setup lang="ts">
/**
 * Une rangée de gestes (portage de `rangeeGestes`, S:4911-4924) : quand plus
 * d'un geste est fermé, que tous les fermés sont visibles et disent la même
 * phrase, la raison s'écrit UNE fois, après les boutons inactifs, avec
 * l'étiquette « Avancé » si c'est le niveau qui ferme. Sinon chaque geste se
 * rend seul, par `BoutonGeste`.
 *
 * Accessible comme `BoutonGeste` : un bouton inactif porte `aria-disabled`,
 * reste atteignable au clavier, et chacun annonce la raison commune par
 * `aria-describedby`.
 */
// ================================
// IMPORTS
// ================================
import { computed, useId } from "vue"
import {
    ETIQUETTE_AVANCE,
    raisonCommune,
    type Acces,
} from "@modules/alarmes-v2/acces/acces"
import BoutonGeste from "./BoutonGeste.vue"

/** Un geste de la rangée. */
export interface GesteDeRangee {
    /** Le verdict du geste (`useAcces().acces(cle)`). */
    acces: Acces
    libelle: string
    classe?: string
}

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<{
    gestes: GesteDeRangee[]
}>()

const emit = defineEmits<{
    /** Un geste actif a été demandé, nommé par sa clé de la matrice. */
    geste: [cle: string, evenement: MouseEvent]
}>()

/** La classe d'un bouton sans classe donnée (celle de `BoutonGeste`). */
const CLASSE_PAR_DEFAUT =
    "rounded border border-surface-300 px-3 py-1.5 text-sm font-medium text-surface-800"

/** L'identifiant de la raison commune, lu par `aria-describedby`. */
const idRaison = `rangee-raison-${useId()}`

// ================================
// COMPUTED
// ================================
/** La raison commune, ou `null` quand chaque geste dit la sienne. */
const getCommune = computed(() =>
    raisonCommune(props.gestes.map((g) => g.acces))
)

// ================================
// HANDLERS
// ================================
function onClick(g: GesteDeRangee, evenement: MouseEvent): void {
    if (g.acces.ok) emit("geste", g.acces.cle, evenement)
}
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <span
        class="inline-flex max-w-full flex-wrap items-center gap-x-1.5 gap-y-0.5"
        data-test="rangee"
    >
        <template v-if="!getCommune">
            <BoutonGeste
                v-for="g in gestes"
                :key="g.acces.cle"
                :acces="g.acces"
                :libelle="g.libelle"
                :classe="g.classe"
                @click="onClick(g, $event)"
            />
        </template>
        <template v-else>
            <template v-for="g in gestes" :key="g.acces.cle">
                <button
                    v-if="g.acces.ok"
                    type="button"
                    :class="g.classe ?? CLASSE_PAR_DEFAUT"
                    data-test="geste"
                    @click="onClick(g, $event)"
                >
                    {{ g.libelle }}
                </button>
                <button
                    v-else-if="g.acces.affichage === 'inactif'"
                    type="button"
                    :class="[
                        g.classe ?? CLASSE_PAR_DEFAUT,
                        'cursor-not-allowed opacity-60',
                    ]"
                    aria-disabled="true"
                    :aria-describedby="idRaison"
                    data-test="geste"
                    @click.prevent
                >
                    {{ g.libelle }}
                </button>
            </template>
            <span
                class="inline-flex flex-wrap items-center gap-x-1.5"
                data-test="geste-ferme"
            >
                <span
                    v-if="getCommune.etiquetteAvance"
                    class="inline-flex items-center whitespace-nowrap rounded-full border border-primary-200 bg-primary-50 px-2 text-xs font-semibold text-primary-700"
                    data-test="etiquette-avance"
                >
                    {{ ETIQUETTE_AVANCE }}
                </span>
                <span
                    :id="idRaison"
                    class="text-xs text-surface-500"
                    data-test="raison"
                >
                    {{ getCommune.raison }}
                </span>
            </span>
        </template>
    </span>
</template>
